import React from "react";
import { eur, formatujPercentoBezZnamienkaText, } from "../../utils/formatovanie";
const RENTA_AUTOMAT_ROKY = 20;
const RENTA_AUTOMAT_ZHODNOTENIE = 4;

function cislo(hodnota) {
  const vysledok = Number(hodnota);
  return Number.isFinite(vysledok) ? vysledok : 0;
}

function orez(hodnota, min, max) {
  return Math.min(max, Math.max(min, cislo(hodnota)));
}

function formatujSumu(hodnota) {
  return `${eur(Math.round(cislo(hodnota)))} €`;
}

function formatujRenta(hodnota) {
  const cisloHodnota = cislo(hodnota);

  if (cisloHodnota <= 0) return "—";

  return `${eur(Math.round(cisloHodnota))} €/mes.`;
}

function formatujPercento(hodnota) {
  return formatujPercentoBezZnamienkaText(cislo(hodnota));
}

function formatujRoky(hodnota) {
  return `${Math.round(orez(hodnota, 0, 50))} r`;
}

function vypocitajMesacnuRentu({ kapital, roky, zhodnotenie }) {
  const kapitalHodnota = orez(kapital, 0, 9999999);
  const pocetMesiacov = Math.round(orez(roky, 0, 30) * 12);
  const rocneZhodnotenie = orez(zhodnotenie, 0, 10) / 100;

  if (kapitalHodnota <= 0 || pocetMesiacov <= 0) return 0;

  const mesacneZhodnotenie = Math.pow(1 + rocneZhodnotenie, 1 / 12) - 1;

  if (mesacneZhodnotenie <= 0) {
    return kapitalHodnota / pocetMesiacov;
  }

  return (
    (kapitalHodnota * mesacneZhodnotenie) /
    (1 - Math.pow(1 + mesacneZhodnotenie, -pocetMesiacov))
  );
}

function vypocitajPotrebnyKapital({ renta, roky, zhodnotenie }) {
  const rentaHodnota = orez(renta, 0, 9999);
  const pocetMesiacov = Math.round(orez(roky, 0, 30) * 12);
  const rocneZhodnotenie = orez(zhodnotenie, 0, 10) / 100;

  if (rentaHodnota <= 0 || pocetMesiacov <= 0) return 0;

  const mesacneZhodnotenie = Math.pow(1 + rocneZhodnotenie, 1 / 12) - 1;

  if (mesacneZhodnotenie <= 0) {
    return rentaHodnota * pocetMesiacov;
  }

  return (
    (rentaHodnota *
      (1 - Math.pow(1 + mesacneZhodnotenie, -pocetMesiacov))) /
    mesacneZhodnotenie
  );
}

function vypocitajRealnuRentu({ renta, inflacia, rokyDoRenty }) {
  const rentaHodnota = cislo(renta);
  const inflaciaHodnota = orez(inflacia, 0, 99);
  const roky = Math.max(0, cislo(rokyDoRenty));

  if (rentaHodnota <= 0 || inflaciaHodnota <= 0 || roky <= 0) return 0;

  return rentaHodnota / Math.pow(1 + inflaciaHodnota / 100, roky);
}

function RentaMetrika({ label, value, muted = false }) {
  return (
    <div className={`invest-renta-metrika ${muted ? "is-muted" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function RentaInvesticnePozadie() {
  return (
    <svg
      className="invest-renta-card-background"
      viewBox="0 0 320 170"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <g className="invest-renta-card-background-bars">
        <rect x="8" y="140" width="18" height="26" rx="2" />
        <rect x="34" y="130" width="18" height="36" rx="2" />
        <rect x="60" y="136" width="18" height="30" rx="2" />
        <rect x="86" y="116" width="18" height="50" rx="2" />
        <rect x="112" y="122" width="18" height="44" rx="2" />
        <rect x="138" y="98" width="18" height="68" rx="2" />
        <rect x="164" y="104" width="18" height="62" rx="2" />
        <rect x="190" y="82" width="18" height="84" rx="2" />
        <rect x="216" y="88" width="18" height="78" rx="2" />
        <rect x="242" y="62" width="18" height="104" rx="2" />
        <rect x="268" y="42" width="18" height="124" rx="2" />
        <rect x="294" y="30" width="18" height="136" rx="2" />
      </g>

      <path
        className="invest-renta-card-background-line"
        d="M14 137 L43 126 L68 131 L95 110 L121 116 L147 92 L173 98 L199 76 L225 81 L251 56 L278 37 L302 24"
      />

      <path
        className="invest-renta-card-background-arrow"
        d="M292 21 L307 21 L304 36 Z"
      />
    </svg>
  );
}

function RentaRealneBlok({ zobraz, rokyDoRenty, value, muted = false }) {
  if (!zobraz) return null;

  return (
    <div className={`invest-renta-realne ${muted ? "is-muted" : ""}`}>
      <span>Reálne po {formatujRoky(rokyDoRenty)}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function InvesticneRentaVystupKarta({
  investRentaOpen,
  aktivnyVariantLabel,
  cielovaSuma,
  vysledok,
  rentaKapital,
  rentaKapitalRoky,
  rentaKapitalZhodnotenie,
  rentaMesacne,
  rentaMesacneRoky,
  rentaMesacneZhodnotenie,
  inflacia,
}) {
  const carouselRef = React.useRef(null);
  const [mozeDolava, setMozeDolava] = React.useState(false);
  const [mozeDoprava, setMozeDoprava] = React.useState(false);
  const [aktivnaStrana, setAktivnaStrana] = React.useState(0);
  const [pocetStran, setPocetStran] = React.useState(1);

  const skontrolujCarousel = React.useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const karty = Array.from(carousel.children);
    const maximalnyPosun = Math.max(
      0,
      carousel.scrollWidth - carousel.clientWidth,
    );

    const krok =
      karty.length > 1
        ? karty[1].offsetLeft - karty[0].offsetLeft
        : carousel.clientWidth;

    const viditelneKarty =
      karty.length && krok > 0
        ? Math.max(
            1,
            Math.round(
              (carousel.clientWidth + (krok - karty[0].offsetWidth)) / krok,
            ),
          )
        : 1;

    const novyPocetStran = Math.max(
      1,
      karty.length - viditelneKarty + 1,
    );

    const novaAktivnaStrana =
      krok > 0
        ? Math.min(
            novyPocetStran - 1,
            Math.max(0, Math.round(carousel.scrollLeft / krok)),
          )
        : 0;

    setMozeDolava(carousel.scrollLeft > 2);
    setMozeDoprava(carousel.scrollLeft < maximalnyPosun - 2);
    setAktivnaStrana(novaAktivnaStrana);
    setPocetStran(novyPocetStran);
  }, []);

  React.useEffect(() => {
    if (!investRentaOpen) return undefined;

    const carousel = carouselRef.current;
    if (!carousel) return undefined;

    const frame = window.requestAnimationFrame(skontrolujCarousel);
    const onScroll = () => skontrolujCarousel();

    carousel.addEventListener("scroll", onScroll, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(skontrolujCarousel)
        : null;

    resizeObserver?.observe(carousel);

    Array.from(carousel.children).forEach((karta) => {
      resizeObserver?.observe(karta);
    });

    window.addEventListener("resize", skontrolujCarousel);

    return () => {
      window.cancelAnimationFrame(frame);
      carousel.removeEventListener("scroll", onScroll);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", skontrolujCarousel);
    };
  }, [investRentaOpen, skontrolujCarousel]);

  const posunNaStranu = React.useCallback(
    (strana) => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      const karty = Array.from(carousel.children);

      const krok =
        karty.length > 1
          ? karty[1].offsetLeft - karty[0].offsetLeft
          : carousel.clientWidth;

      const cielovaStrana = Math.min(
        pocetStran - 1,
        Math.max(0, strana),
      );

      carousel.scrollTo({
        left: cielovaStrana * krok,
        behavior: "smooth",
      });
    },
    [pocetStran],
  );

  const ovladanieKlavesnicou = (event) => {
    if (event.key === "ArrowLeft" && mozeDolava) {
      event.preventDefault();
      posunNaStranu(aktivnaStrana - 1);
    }

    if (event.key === "ArrowRight" && mozeDoprava) {
      event.preventDefault();
      posunNaStranu(aktivnaStrana + 1);
    }
  };

  if (!investRentaOpen) return null;

  const kapitalZCielovejSumy = Math.max(0, cislo(cielovaSuma));
  const kapitalZInvesticie = Math.max(
    0,
    cislo(vysledok?.konecnaHodnota),
  );

  const rentaZCielovejSumy = vypocitajMesacnuRentu({
    kapital: kapitalZCielovejSumy,
    roky: RENTA_AUTOMAT_ROKY,
    zhodnotenie: RENTA_AUTOMAT_ZHODNOTENIE,
  });

  const realnaRentaZCielovejSumy = vypocitajRealnuRentu({
    renta: rentaZCielovejSumy,
    inflacia,
    rokyDoRenty: RENTA_AUTOMAT_ROKY,
  });

  const rentaZInvesticie = vypocitajMesacnuRentu({
    kapital: kapitalZInvesticie,
    roky: RENTA_AUTOMAT_ROKY,
    zhodnotenie: RENTA_AUTOMAT_ZHODNOTENIE,
  });

  const realnaRentaZInvesticie = vypocitajRealnuRentu({
    renta: rentaZInvesticie,
    inflacia,
    rokyDoRenty: RENTA_AUTOMAT_ROKY,
  });

  const rentaZKapitalu = vypocitajMesacnuRentu({
    kapital: rentaKapital,
    roky: rentaKapitalRoky,
    zhodnotenie: rentaKapitalZhodnotenie,
  });

  const realnaRentaZKapitalu = vypocitajRealnuRentu({
    renta: rentaZKapitalu,
    inflacia,
    rokyDoRenty: rentaKapitalRoky,
  });

  const potrebnyKapital = vypocitajPotrebnyKapital({
    renta: rentaMesacne,
    roky: rentaMesacneRoky,
    zhodnotenie: rentaMesacneZhodnotenie,
  });

  const realnaZadanaRenta = vypocitajRealnuRentu({
    renta: rentaMesacne,
    inflacia,
    rokyDoRenty: rentaMesacneRoky,
  });

  const zobrazInflaciu = cislo(inflacia) > 0;

  const automatRokyText =
  `Dĺžka vyplácania ${formatujRoky(RENTA_AUTOMAT_ROKY)} ` +
  `pri ${formatujPercento(RENTA_AUTOMAT_ZHODNOTENIE)} p.a.`;

  const kapitalRokyText =
    `Dĺžka vyplácania ${formatujRoky(rentaKapitalRoky)} ` +
    `pri ${formatujPercento(rentaKapitalZhodnotenie)} p.a.`;

  const rentaRokyText =
    `Dĺžka vyplácania ${formatujRoky(rentaMesacneRoky)} ` +
    `pri ${formatujPercento(rentaMesacneZhodnotenie)} p.a.`;

  return (
    <div className="card-section card-section--invest-renta card-section--invest-renta-vystup">
      <h2>Výstup renty</h2>

      <div className="invest-renta-carousel">
        <div className="invest-renta-carousel-row">
          <button
            type="button"
            className="invest-renta-carousel-arrow"
            onClick={() => posunNaStranu(aktivnaStrana - 1)}
            disabled={!mozeDolava}
            aria-label="Predchádzajúce výsledky renty"
          >
            ‹
          </button>

          <div
            ref={carouselRef}
            className="invest-renta-carousel-track"
            onKeyDown={ovladanieKlavesnicou}
            role="region"
            aria-label="Výsledky renty"
            tabIndex={0}
          >
            <article className="invest-renta-vystup-card">
              <RentaInvesticnePozadie />

              <div className="invest-renta-card-head">
                <span>Z hlavnej investície</span>
                <strong>{aktivnyVariantLabel}</strong>
              </div>

              <div className="invest-renta-card-body invest-renta-card-body--auto">
                <div className="invest-renta-auto-blok">
                  <RentaMetrika
                    label="Cieľová suma"
                    value={
                      kapitalZCielovejSumy > 0
                        ? formatujSumu(kapitalZCielovejSumy)
                        : "—"
                    }
                    muted={kapitalZCielovejSumy <= 0}
                  />

                  <strong className="invest-renta-card-hero">
                    {formatujRenta(rentaZCielovejSumy)}
                  </strong>

                  <RentaRealneBlok
                    zobraz={
                      zobrazInflaciu && kapitalZCielovejSumy > 0
                    }
                    rokyDoRenty={RENTA_AUTOMAT_ROKY}
                    value={formatujRenta(
                      realnaRentaZCielovejSumy,
                    )}
                    muted={realnaRentaZCielovejSumy <= 0}
                  />
                </div>

                <div className="invest-renta-auto-blok">
                  <RentaMetrika
                    label="Hodnota investície"
                    value={
                      kapitalZInvesticie > 0
                        ? formatujSumu(kapitalZInvesticie)
                        : "—"
                    }
                    muted={kapitalZInvesticie <= 0}
                  />

                  <strong className="invest-renta-card-hero">
                    {formatujRenta(rentaZInvesticie)}
                  </strong>

                  <RentaRealneBlok
                    zobraz={
                      zobrazInflaciu && kapitalZInvesticie > 0
                    }
                    rokyDoRenty={RENTA_AUTOMAT_ROKY}
                    value={formatujRenta(realnaRentaZInvesticie)}
                    muted={realnaRentaZInvesticie <= 0}
                  />
                </div>
              </div>

              <div className="invest-renta-card-foot">
                {automatRokyText}
              </div>
            </article>

            <article className="invest-renta-vystup-card">
              <RentaInvesticnePozadie />

              <div className="invest-renta-card-head">
                <span>Z kapitálu</span>

                <strong className="invest-renta-card-secondary">
                  {formatujSumu(rentaKapital)}
                </strong>
              </div>

              <div className="invest-renta-card-main">
                <span>Mesačná renta</span>
                <strong>{formatujRenta(rentaZKapitalu)}</strong>
              </div>

              <RentaRealneBlok
                zobraz={ zobrazInflaciu && cislo(rentaKapitalRoky) > 0 }
                rokyDoRenty={rentaKapitalRoky}
                value={formatujRenta(realnaRentaZKapitalu)}
                muted={realnaRentaZKapitalu <= 0}
              />

              <div className="invest-renta-card-foot">
                {kapitalRokyText}
              </div>
            </article>

            <article className="invest-renta-vystup-card">
              <RentaInvesticnePozadie />

              <div className="invest-renta-card-head">
                <span>Požadovaná renta</span>

                <strong className="invest-renta-card-secondary">
                  {formatujRenta(rentaMesacne)}
                </strong>
              </div>

              <div className="invest-renta-card-main">
                <span>Vložiť jednorazovo</span>
                <strong>
                  {potrebnyKapital > 0
                    ? formatujSumu(potrebnyKapital)
                    : "—"}
                </strong>
              </div>

              <RentaRealneBlok
                zobraz={ zobrazInflaciu && cislo(rentaMesacneRoky) > 0 }
                rokyDoRenty={rentaMesacneRoky}
                value={formatujRenta(realnaZadanaRenta)}
                muted={realnaZadanaRenta <= 0}
              />

              <div className="invest-renta-card-foot">
                {rentaRokyText}
              </div>
            </article>
          </div>

          <button
            type="button"
            className="invest-renta-carousel-arrow"
            onClick={() => posunNaStranu(aktivnaStrana + 1)}
            disabled={!mozeDoprava}
            aria-label="Nasledujúce výsledky renty"
          >
            ›
          </button>
        </div>

        {pocetStran > 1 && (
          <div
            className="invest-renta-carousel-dots"
            aria-label="Pozícia vo výsledkoch renty"
          >
            {Array.from({ length: pocetStran }, (_, index) => (
              <button
                key={index}
                type="button"
                className={`invest-renta-carousel-dot ${
                  index === aktivnaStrana ? "is-active" : ""
                }`}
                onClick={() => posunNaStranu(index)}
                aria-label={`Zobraziť stranu ${index + 1}`}
                aria-current={
                  index === aktivnaStrana ? "true" : undefined
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}